import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  switchMap,
  map,
  tap,
  distinctUntilChanged,
  Observable,
  of,
} from 'rxjs';

import { SelectedSubproject } from '@shared/models';
import { AutoSaveableComponent } from '@shared/models/component';
import { DprDetails } from '@shared/models/dpr';
import { StatusTracker } from '@shared/models/project-setup';
import { OfflineFacade, UserDetailsFacade } from '@shared/state/facades';
import { ScreenSizeService } from '@shared/services';

@UntilDestroy()
@Component({
  selector: 'app-dpr-status-tracker-page',
  templateUrl: './dpr-status-tracker-page.component.html',
  styleUrls: ['./dpr-status-tracker-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprStatusTrackerPageComponent
  implements OnInit, AutoSaveableComponent
{
  selectedSubproject$ = new BehaviorSubject<SelectedSubproject>(null);
  dprDetails$ = new BehaviorSubject<DprDetails>(null);

  preselectedStatusTrackerId: number;

  private activeAutoSaveableComponent: AutoSaveableComponent;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private userDetailsFacade: UserDetailsFacade,
    private offlineFacade: OfflineFacade,
    private location: Location,
    protected screenSizeService: ScreenSizeService
  ) {}

  private get statusTrackerFromRoute(): string {
    return this.location.path().split('/').splice(6)[0];
  }

  ngOnInit(): void {
    this.activatedRoute?.firstChild?.params.subscribe((params) => {
      this.preselectedStatusTrackerId = +params.statusTrackerId;
    });

    this.userDetailsFacade.query.selectedSubproject$
      .pipe(
        untilDestroyed(this),
        tap((subproject) => {
          this.selectedSubproject$.next(subproject);

          const statusTrackers = subproject?.data?.statusTrackers ?? [];

          // if status tracker list is empty navigate to base page
          if (!statusTrackers.length) {
            this.router.navigate(['./'], {
              relativeTo: this.activatedRoute,
              queryParamsHandling: 'merge',
              replaceUrl: true,
            });
            return;
          }

          this.preselectedStatusTrackerId = +this.statusTrackerFromRoute;

          const isSelectedStatusTrackerActive = statusTrackers
            .filter((x) => x.isActive)
            .map((x) => x.id)
            .includes(this.preselectedStatusTrackerId);

          // This is necessary in order to automatically redirect to the first status tracker if its missing from the url
          if (
            statusTrackers?.length &&
            (!this.preselectedStatusTrackerId || !isSelectedStatusTrackerActive)
          ) {
            if (statusTrackers.filter((x) => x.isActive).length) {
              this.router.navigate(
                [statusTrackers.filter((x) => x.isActive)[0].id],
                {
                  relativeTo: this.activatedRoute,
                  queryParamsHandling: 'merge',
                  replaceUrl: true,
                }
              );
            } else {
              this.router.navigate(['./'], {
                relativeTo: this.activatedRoute,
                queryParamsHandling: 'merge',
                replaceUrl: true,
              });
            }
          }
        })
      )
      .subscribe();

    // this is needed for back and forth navigation between dprs as the dpr id is changed and conditions must be recomputed
    this.activatedRoute.parent.params
      .pipe(
        untilDestroyed(this),
        map((params) => +params.dprDetailId),
        distinctUntilChanged(),
        switchMap((dprDetailId) =>
          this.offlineFacade.getDprDetails(dprDetailId)
        ),
        tap((dprDetails) => {
          this.dprDetails$.next(dprDetails);
        })
      )
      .subscribe();
  }

  autoSave(): void {
    if (this.activeAutoSaveableComponent) {
      this.activeAutoSaveableComponent.autoSave();
    }
  }

  autoSaveFromAdminChange(): Observable<boolean> {
    if (this.activeAutoSaveableComponent) {
      return this.activeAutoSaveableComponent.autoSaveFromAdminChange();
    }

    return of(false);
  }

  componentActivated(component: Component): void {
    const autoSaveableComponent = component as AutoSaveableComponent;
    if (
      autoSaveableComponent.autoSave &&
      autoSaveableComponent.autoSaveFromAdminChange
    ) {
      this.activeAutoSaveableComponent = component as AutoSaveableComponent;
    }
  }

  componentDeactivated(): void {
    this.activeAutoSaveableComponent = null;
  }

  trackStatusTracker(_index: number, statusTracker: StatusTracker): number {
    return +statusTracker?.id;
  }

  changePreselectedStatusTrackerId(id: number): void {
    this.preselectedStatusTrackerId = id;
  }
}
